import { useEffect, useRef } from 'react';

interface Props {
  /** Number of floating nodes. */
  count?: number;
  /** Hex stroke + fill base for nodes/edges. */
  color?: string;
  /** Speed multiplier for node drift + pulse rate. 1 = original tempo,
   *  2 = roughly twice as fast. Defaults to 1.8 so the atom feels alive
   *  rather than meditative. */
  speed?: number;
  /** Optional CSS classname for the canvas wrapper. */
  className?: string;
  /** Style overrides for the canvas. */
  style?: React.CSSProperties;
}

interface NetNode { x: number; y: number; vx: number; vy: number; r: number; }
interface Pulse { a: number; b: number; t: number; life: number; }

/**
 * Animated "atom" canvas — floating dots connected by thin lines, with
 * occasional bright pulses traveling along edges. Same effect used on the
 * Ruby Till landing-page hero (NodeNetwork in pages/rubytill-landing/
 * components/AnimatedGraphics.tsx). Self-pauses for users with prefers-
 * reduced-motion enabled.
 *
 * The color string drives BOTH the line/dot fill and the pulse color, so a
 * single hex value (default crimson) themes the whole effect.
 */
export function NodeNetwork({
  count = 22,
  color = '#DC143C',
  speed = 1.8,
  className,
  style,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Pre-parse the hex once so we can mix it into rgba() strings each frame
  // without paying the regex cost per pixel.
  const rgb = parseHex(color);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0, h = 0, dpr = 1;
    let nodes: NetNode[] = [];
    let pulses: Pulse[] = [];
    let raf = 0;

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      dpr = Math.min(2, window.devicePixelRatio || 1);
      w = r.width;
      h = r.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const init = () => {
      nodes = Array.from({ length: count }).map(() => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.18 * speed,
        vy: (Math.random() - 0.5) * 0.18 * speed,
        r: 1 + Math.random() * 1.4,
      }));
    };

    const spawnPulse = () => {
      if (nodes.length < 2) return;
      const a = (Math.random() * nodes.length) | 0;
      let b = (Math.random() * nodes.length) | 0;
      if (b === a) b = (b + 1) % nodes.length;
      // Pulse life shortens with speed → pulses cross edges faster.
      pulses.push({ a, b, t: 0, life: (90 + Math.random() * 60) / speed });
    };

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      // Drift nodes; bounce off canvas edges.
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }
      // Edges between any pair within 180px, opacity fades with distance.
      const maxD = 180;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < maxD) {
            const op = (1 - d / maxD) * 0.18;
            ctx.strokeStyle = `rgba(${rgb}, ${op})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      // Each node renders as a solid dot with a softer halo behind it.
      for (const n of nodes) {
        ctx.fillStyle = `rgba(${rgb}, 0.55)`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(${rgb}, 0.12)`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r * 3, 0, Math.PI * 2);
        ctx.fill();
      }
      // Pulses travel along edges and fade out at their endpoints.
      pulses = pulses.filter(p => {
        p.t++;
        if (p.t > p.life) return false;
        const a = nodes[p.a], b = nodes[p.b];
        if (!a || !b) return false;
        const k = p.t / p.life;
        const x = a.x + (b.x - a.x) * k;
        const y = a.y + (b.y - a.y) * k;
        const fade = Math.sin(k * Math.PI);
        ctx.fillStyle = `rgba(255, 100, 130, ${0.9 * fade})`;
        ctx.shadowColor = color;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.arc(x, y, 2.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        return true;
      });
      // Pulse spawn rate scales with speed too — denser shimmer at higher speeds.
      if (Math.random() < 0.04 * speed && pulses.length < 12) spawnPulse();
      if (!reducedMotion) raf = requestAnimationFrame(tick);
    };

    resize();
    init();
    if (!reducedMotion) raf = requestAnimationFrame(tick);
    else tick();
    const ro = new ResizeObserver(() => { resize(); init(); });
    ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [count, color, rgb, speed]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        // Vignette so the edges fade into the page bg, no hard cutoff.
        WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 40%, black 30%, transparent 85%)',
        maskImage: 'radial-gradient(ellipse 80% 70% at 50% 40%, black 30%, transparent 85%)',
        ...style,
      }}
    />
  );
}

/** Convert a #RRGGBB hex string to a comma-joined `r, g, b` string for use
 *  inside `rgba(...)` expressions. Falls back to crimson on parse failure
 *  so the canvas always renders something visible. */
function parseHex(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '220, 20, 60';
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 0xff}, ${(n >> 8) & 0xff}, ${n & 0xff}`;
}
